import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays } from "date-fns";
import { api } from "../lib/api";
import { ymd } from "../lib/date";
import type { TimeOffType } from "../lib/types";
import { TIME_OFF_LABEL } from "../lib/types";
import { Button } from "../components/ui/Button";
import { Field, Input, Select, Textarea } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import {
  InlineRequestFieldManager, RequestFieldList,
} from "../components/RequestFields";
import { RequestSection, requestMatchesSearch } from "../components/RequestList";
import { PrintableForms } from "../components/PrintableForms";
import { PrintRangeDialog } from "../components/PrintRangeDialog";
import { RequestDetailModal } from "../components/RequestDetailModal";
import { useEffectiveUser } from "../lib/auth";
import { can } from "../lib/permissions";
import { visibleRequestsFor } from "../lib/requestVisibility";

export function TimeOffPage() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const canPickAnyone = can.editRoster(me);
  const canManageFields = can.viewSettings(me);
  const start = ymd(addDays(new Date(), -30));
  const end = ymd(addDays(new Date(), 90));

  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const shifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const items = useQuery({
    queryKey: ["timeoff", start, end],
    queryFn: () => api.listTimeOff(start, end),
  });
  const timeOffFields = useQuery({
    queryKey: ["requestFields", "time_off"],
    queryFn: () => api.listRequestFields("time_off"),
  });
  // Used by the detail modal to render reviewer names from the user
  // id stored on reviewed_by.
  const users = useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
    enabled: canPickAnyone,
  });

  const empById = useMemo(
    () => Object.fromEntries((employees.data ?? []).map((e) => [e.id, e])),
    [employees.data],
  );
  const shiftById = useMemo(
    () => Object.fromEntries((shifts.data ?? []).map((s) => [s.id, s])),
    [shifts.data],
  );
  const usersById = useMemo(
    () => Object.fromEntries((users.data ?? []).map((u) => [u.id, u])),
    [users.data],
  );

  // Training requests have their own page now; filter them out
  // here. Custom-form submissions (Settings → Forms) also have their
  // own page each and are keyed by form_slug — filter those out too
  // so this page stays focused on time-off. Visibility: Owner / L1
  // see everything; L2 is scoped to officers in their own home
  // department; L3 / L4 see only their own.
  const visible = useMemo(() => {
    const scoped = (items.data ?? [])
      .filter((t) => t.type !== "training" && !t.formSlug);
    return visibleRequestsFor(me, scoped, empById);
  }, [items.data, me, empById]);

  // Controls: free-text search + officer / type / shift filters.
  // Apply them before splitting into Active / Completed so both
  // sections reflect the same working set. Picking a specific shift
  // flips the list off grouped mode and renders a flat list.
  const [searchQuery, setSearchQuery] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<"all" | TimeOffType>("all");
  const [shiftFilter, setShiftFilter] = useState<string>("");

  const filtered = useMemo(() => {
    return visible.filter((t) => {
      if (typeFilter !== "all" && t.type !== typeFilter) return false;
      if (employeeFilter && t.employeeId !== employeeFilter) return false;
      if (shiftFilter) {
        const empShift = empById[t.employeeId]?.homeShiftId ?? "__unassigned__";
        if (shiftFilter !== empShift) return false;
      }
      return requestMatchesSearch(t, searchQuery, empById);
    });
  }, [visible, searchQuery, typeFilter, employeeFilter, shiftFilter, empById]);

  const filterActive =
    searchQuery.trim() !== ""
    || employeeFilter !== ""
    || typeFilter !== "all"
    || shiftFilter !== "";

  // Active = still awaiting a decision; Completed = approved or
  // denied. Splits the UI into two sections so supervisors can focus
  // on the queue of pending requests without scrolling past closed
  // ones. Completed flips to most-recent-first.
  // Active = not yet finalised. l2_approved isn't reachable from
  // the time-off flow today (only training uses the two-stage
  // approval), but include it defensively so a stray row from an
  // imported dataset still shows up where the supervisor can act
  // on it.
  const active = useMemo(
    () => filtered.filter((t) => t.status === "requested" || t.status === "l2_approved"),
    [filtered],
  );
  const completed = useMemo(
    () => filtered
      .filter((t) => t.status === "approved" || t.status === "denied")
      .slice()
      .sort((a, b) => b.startDate.localeCompare(a.startDate)),
    [filtered],
  );

  // Detail modal — clicking any row opens it. Approve / Deny live
  // inside the modal for pending requests; completed requests get a
  // Print / PDF button that exports just that one form.
  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = useMemo(
    () => visible.find((t) => t.id === viewingId) ?? null,
    [visible, viewingId],
  );

  // Per-request print: setting printOnlyId narrows the printable
  // output to that single form, then a useEffect kicks the browser's
  // print dialog and resets the state when it closes.
  const [printOnlyId, setPrintOnlyId] = useState<string | null>(null);
  useEffect(() => {
    if (!printOnlyId) return;
    const reset = () => setPrintOnlyId(null);
    window.addEventListener("afterprint", reset, { once: true });
    const t = setTimeout(() => window.print(), 50);
    return () => {
      clearTimeout(t);
      window.removeEventListener("afterprint", reset);
    };
  }, [printOnlyId]);

  // Date-range gate for the page-level "Print completed" button.
  // printRange null → not printing-by-range. When set, the
  // PrintableForms component below switches to a filtered
  // completed-by-range list and a useEffect fires window.print()
  // on the next tick, mirroring the printOnlyId flow above.
  const [printRangeOpen, setPrintRangeOpen] = useState(false);
  const [printRange, setPrintRange] = useState<{
    start: string; end: string;
  } | null>(null);
  const requestsForPrint = useMemo(() => {
    if (!printRange) return completed;
    return completed.filter(
      (t) => t.startDate >= printRange.start && t.startDate <= printRange.end,
    );
  }, [completed, printRange]);
  useEffect(() => {
    if (!printRange) return;
    const reset = () => setPrintRange(null);
    window.addEventListener("afterprint", reset, { once: true });
    const t = setTimeout(() => window.print(), 50);
    return () => {
      clearTimeout(t);
      window.removeEventListener("afterprint", reset);
    };
  }, [printRange]);

  const employeeOptions = useMemo(
    () => (employees.data ?? [])
      .filter((e) => e.status !== "inactive")
      .slice()
      .sort((a, b) =>
        (a.lastName ?? "").localeCompare(b.lastName ?? "")
        || (a.firstName ?? "").localeCompare(b.firstName ?? "")),
    [employees.data],
  );
  const shiftOptions = useMemo(
    () => (shifts.data ?? [])
      .filter((s) => !s.archivedAt)
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder),
    [shifts.data],
  );

  // Show every TimeOffType except training (that has its own page).
  const typeOptions: TimeOffType[] = [
    "vacation", "sick", "comp", "court", "personal", "military", "fmla", "other",
  ];

  const [draft, setDraft] = useState<{
    employeeId: string;
    startDate: string;
    endDate: string;
    // Partial-hours mode: when true the "From" / "To" fields render
    // and their HH:MM values submit as start_time / end_time. Off by
    // default so the common whole-day request stays a single click.
    partial: boolean;
    startTime: string;
    endTime: string;
    type: TimeOffType;
    note: string;
    metadata: Record<string, string | number | boolean | null>;
  } | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);

  function openDraft() {
    const initialMeta: Record<string, string | number | boolean | null> = {};
    for (const f of timeOffFields.data ?? []) {
      initialMeta[f.key] = f.fieldType === "checkbox" ? false : "";
    }
    setDraft({
      employeeId: me?.employeeId ?? employees.data?.[0]?.id ?? "",
      startDate: ymd(new Date()),
      endDate: ymd(new Date()),
      partial: false,
      startTime: "",
      endTime: "",
      type: "vacation",
      note: "",
      metadata: initialMeta,
    });
    setDraftError(null);
  }

  const createMut = useMutation({
    mutationFn: () => api.requestTimeOff({
      employeeId: draft!.employeeId,
      startDate: draft!.startDate,
      endDate: draft!.endDate,
      startTime: draft!.partial ? draft!.startTime : null,
      endTime: draft!.partial ? draft!.endTime : null,
      type: draft!.type,
      note: draft!.note || null,
      metadata: draft!.metadata,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timeoff"] });
      setDraft(null);
      setDraftError(null);
    },
  });

  const reviewMut = useMutation({
    mutationFn: ({ id, approved }: { id: string; approved: boolean }) =>
      api.reviewTimeOff(id, approved),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timeoff"] });
      // Approval / denial moves the approved-overlay on the Schedule
      // and changes coverage contributions, so refresh those caches
      // too.
      qc.invalidateQueries({ queryKey: ["coverage"] });
    },
  });

  // The sidebar "Request time off" shortcut navigates here with
  // location.state.openNew = true. Auto-open the draft for the
  // signed-in user, then clear the flag so refreshing doesn't
  // keep re-opening it.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const state = location.state as { openNew?: boolean } | null;
    if (!state?.openNew) return;
    if (!me?.employeeId && (employees.data?.length ?? 0) === 0) return;
    openDraft();
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate, me?.employeeId, employees.data]);

  return (
    <div className="p-4 md:p-6">
      <header className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-4 print:hidden">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Time off</h1>
          <p className="text-sm text-slate-500">
            {canPickAnyone
              ? "Requests in the last 30 and next 90 days."
              : "Your time-off requests in the last 30 and next 90 days."}
          </p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button
            variant="secondary"
            onClick={() => setPrintRangeOpen(true)}
            disabled={completed.length === 0}
            title={completed.length === 0
              ? "Nothing to print yet — only approved or denied requests export to PDF."
              : "Pick a date range, then export each completed request as a printable form (one per page)."}
          >
            Print completed ({completed.length})
          </Button>
          <Button
            disabled={!me?.employeeId && !canPickAnyone}
            title={!me?.employeeId && !canPickAnyone
              ? "Your account isn't linked to an employee yet. Ask an admin."
              : undefined}
            onClick={openDraft}
          >
            + Time off request
          </Button>
        </div>
      </header>

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center print:hidden">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search name, note, or field value…"
            className="w-full rounded border border-slate-300 bg-white px-3 py-1.5 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-sky-400"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              title="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as "all" | TimeOffType)}
          className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm sm:w-40 focus:outline-none focus:ring-1 focus:ring-sky-400"
        >
          <option value="all">All types</option>
          {typeOptions.map((t) => (
            <option key={t} value={t}>{TIME_OFF_LABEL[t]}</option>
          ))}
        </select>
        <select
          value={shiftFilter}
          onChange={(e) => setShiftFilter(e.target.value)}
          className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm sm:w-44 focus:outline-none focus:ring-1 focus:ring-sky-400"
        >
          <option value="">All shifts</option>
          {shiftOptions.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
          <option value="__unassigned__">Unassigned</option>
        </select>
        {canPickAnyone && (
          <select
            value={employeeFilter}
            onChange={(e) => setEmployeeFilter(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm sm:w-56 focus:outline-none focus:ring-1 focus:ring-sky-400"
          >
            <option value="">All officers</option>
            {employeeOptions.map((e) => (
              <option key={e.id} value={e.id}>{e.lastName}, {e.firstName}</option>
            ))}
          </select>
        )}
      </div>

      <div className="print:hidden">
        <RequestSection
          title="Active"
          count={active.length}
          emptyMessage={filterActive
            ? "No active time-off requests match."
            : "No pending time-off requests."}
          rows={active}
          empById={empById}
          fields={timeOffFields.data ?? []}
          shiftById={shiftById}
          groupByShift={shiftFilter === ""}
          showType
          onView={(t) => setViewingId(t.id)}
        />

        <div className="mt-6">
          <RequestSection
            title="Completed"
            count={completed.length}
            emptyMessage={filterActive
              ? "No completed time-off requests match."
              : "No approved or denied time-off requests in this window."}
            rows={completed}
            empById={empById}
            fields={timeOffFields.data ?? []}
            shiftById={shiftById}
            groupByShift={shiftFilter === ""}
            showType
            onView={(t) => setViewingId(t.id)}
          />
        </div>
      </div>

      <RequestDetailModal
        open={viewing !== null}
        request={viewing}
        employee={viewing ? empById[viewing.employeeId] : undefined}
        fields={timeOffFields.data ?? []}
        kindLabel="Time off"
        canReview={canPickAnyone}
        canFinalize={me?.level === "owner" || me?.level === "l1"}
        onReview={(id, approved) => reviewMut.mutate({ id, approved })}
        onPrint={(id) => { setViewingId(null); setPrintOnlyId(id); }}
        onClose={() => setViewingId(null)}
        usersById={usersById}
        empById={empById}
      />

      <PrintableForms
        agencyName={agency.data?.name ?? "Agency"}
        kindLabel="Time off"
        requests={requestsForPrint}
        fields={timeOffFields.data ?? []}
        empById={empById}
        usersById={usersById}
        onlyId={printOnlyId}
      />

      <PrintRangeDialog
        open={printRangeOpen}
        totalCount={completed.length}
        onConfirm={(range) => {
          setPrintRangeOpen(false);
          setPrintRange(range);
        }}
        onClose={() => setPrintRangeOpen(false)}
      />

      <Modal
        open={draft !== null}
        onClose={() => { setDraft(null); setDraftError(null); }}
        title="New time-off request"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setDraft(null); setDraftError(null); }}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                const missing = (timeOffFields.data ?? []).filter((f) => {
                  if (!f.required) return false;
                  const v = draft?.metadata[f.key];
                  if (f.fieldType === "checkbox") return v !== true;
                  return v === "" || v === null || v === undefined;
                });
                if (missing.length > 0) {
                  setDraftError(`Required: ${missing.map((f) => f.label).join(", ")}`);
                  return;
                }
                setDraftError(null);
                createMut.mutate();
              }}
              disabled={createMut.isPending}
            >
              {createMut.isPending ? "Saving…" : "Submit"}
            </Button>
          </>
        }
      >
        {draft && (
          <div className="space-y-3">
            <Field label="Employee">
              <Select
                value={draft.employeeId}
                onChange={(e) => setDraft({ ...draft, employeeId: e.target.value })}
                disabled={!canPickAnyone}
              >
                {(employees.data ?? []).map((e) => (
                  <option key={e.id} value={e.id}>{e.lastName}, {e.firstName}</option>
                ))}
              </Select>
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Start date">
                <Input type="date" value={draft.startDate}
                  onChange={(e) => setDraft({ ...draft, startDate: e.target.value, endDate: draft.partial ? e.target.value : draft.endDate })} />
              </Field>
              <Field label="End date">
                <Input type="date" value={draft.endDate}
                  onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
                  disabled={draft.partial} />
              </Field>
            </div>
            {/* Partial-hours mode: off by default so the whole-day
              * common case stays a single click. On, the End date
              * collapses to match Start (partial windows are single-
              * day) and From / To time inputs appear. Sends
              * start_time / end_time on the row so the schedule
              * cell can render the reduced range + amber tint and
              * coverage math can subtract the missing hours. */}
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={draft.partial}
                onChange={(e) => setDraft({
                  ...draft,
                  partial: e.target.checked,
                  endDate: e.target.checked ? draft.startDate : draft.endDate,
                })}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              Only part of the day (specific hours)
            </label>
            {draft.partial && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="From">
                  <Input
                    type="time"
                    value={draft.startTime}
                    onChange={(e) => setDraft({ ...draft, startTime: e.target.value })}
                  />
                </Field>
                <Field label="To">
                  <Input
                    type="time"
                    value={draft.endTime}
                    onChange={(e) => setDraft({ ...draft, endTime: e.target.value })}
                  />
                </Field>
              </div>
            )}
            <Field label="Type">
              <Select
                value={draft.type}
                onChange={(e) => setDraft({ ...draft, type: e.target.value as TimeOffType })}
              >
                <option value="vacation">{TIME_OFF_LABEL.vacation}</option>
                <option value="sick">{TIME_OFF_LABEL.sick}</option>
                <option value="comp">{TIME_OFF_LABEL.comp}</option>
                <option value="court">{TIME_OFF_LABEL.court}</option>
                <option value="personal">{TIME_OFF_LABEL.personal}</option>
                <option value="military">{TIME_OFF_LABEL.military}</option>
                <option value="fmla">{TIME_OFF_LABEL.fmla}</option>
                <option value="other">{TIME_OFF_LABEL.other}</option>
              </Select>
            </Field>

            {(timeOffFields.data ?? []).length > 0 && (
              <RequestFieldList
                fields={timeOffFields.data ?? []}
                values={draft.metadata}
                onChange={(k, v) => setDraft({
                  ...draft,
                  metadata: { ...draft.metadata, [k]: v },
                })}
              />
            )}

            {canManageFields && (
              <div className="mt-2 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setManageOpen((v) => !v)}
                  className="text-xs font-medium text-sky-700 hover:text-sky-900"
                >
                  {manageOpen ? "Hide field settings" : "Manage fields…"}
                </button>
                {manageOpen && (
                  <InlineRequestFieldManager
                    scope="time_off"
                    fields={timeOffFields.data ?? []}
                    onChanged={(updater) => {
                      setDraft((d) => d ? { ...d, metadata: updater(d.metadata) } : d);
                    }}
                  />
                )}
              </div>
            )}

            <Field label="Note">
              <Textarea value={draft.note}
                onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
            </Field>
            {draftError && (
              <div className="text-sm text-red-700">{draftError}</div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
